import React, { useCallback, useContext, useEffect, useMemo } from 'react';

import { dashboardBookingInquiryApis } from '@apis/next/bookingInquiryApis';
import { dashboardCustomsRequestApis } from '@apis/next/customsRequestApis';
import { exchangeCalendarApis } from '@apis/next/exchangeCalendarApis';
import { fileUploadApis } from '@apis/next/fileUploadApis';
import { dashboardInvoiceApis } from '@apis/next/invoiceApis';
import { dashboardPaymentSlipApis } from '@apis/next/paymentSlipApis';
import { dashboardPdfApis } from '@apis/next/pdfApis';
import addSvg from '@assets/img/icons/add.svg';
import deleteSvg from '@assets/img/icons/delete.svg';
import lockIcon from '@assets/img/icons/lock.svg';
import pencilGraySvg from '@assets/img/icons/pencil-gray.svg';
import refreshLightSvg from '@assets/img/icons/refresh-light.svg';
import {
  PaymentSlipFormDialogContext,
  PaymentSlipFormDialogContextValues,
  PaymentSlipFormDialogContextValuesBase,
} from '@components/admin/booking/helpers';
import { PartnerLink } from '@components/admin/widgets/PartnerLink';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogActions,
  ActionableDialogTitle,
} from '@components/Dialog';
import { DebugForm } from '@components/NextForms/DebugForm';
import { ErrorMessages } from '@components/NextForms/ErrorMessages';
import { FileInput } from '@components/NextForms/FileInput';
import { IndexedFormContainer } from '@components/NextForms/IndexedForm';
import { IndexedLabel } from '@components/NextForms/IndexedForm';
import { TextInput, type TextInputOption } from '@components/NextForms/TextInput';
import { QueryContainer } from '@components/QueryContainer';
import { customsRequestStrings } from '@data/customsRequest';
import { ledgerDivisions } from '@data/ledger';
import { moneyStrings } from '@data/money';
import { paymentDetailsStrings, paymentSlipStrings } from '@data/paymentSlip';
import { preAlertStrings } from '@data/preAlert';
import { usePayeeIdAutoComplete } from '@hooks/next/usePayeeIdAutoComplete';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { useFormUtils } from '@hooks/useFormUtils';
import { useMutation } from '@hooks/useMutation';
import { FileTypeHelper } from '@models/next/FileType';
import {
  BasePaymentDetailsHelper,
  examplePaymentSlipDetailsForm,
  examplePaymentSlipInfoForm,
  PaymentSlipDetailsForm,
  PaymentSlipInfoForm,
  PaymentSlipHelper,
  PaymentSlipPaymentDatesQuery,
} from '@models/next/PaymentSlip';
import {
  decodeResultEqual,
  empty,
  mapDecodeResult,
  type MobxZodField,
  type MobxZodObjectField,
  unwrapDecodeResult,
  getDecodeResult,
} from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { useQueryClient } from '@tanstack/react-query';
import { foldMessagesEither } from '@utils/errors';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { id, mapOptional } from '@utils/types';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { runInAction } from 'mobx';
import { observer, useLocalObservable } from 'mobx-react';
import { Document, Page } from 'react-pdf';
import { pipe } from 'remeda';
import type { ZodTypeAny } from 'zod';

const strings = createStrings({
  ja: {
    title: () => ({
      create: `${paymentSlipStrings.payment_slip}を作成する`,
      update: `${paymentSlipStrings.payment_slip}を編集する`,
    }),
    preview: 'プレビュー',
    create: '作成',
    update: '編集',
    inputPrompt: `${paymentSlipStrings.payment_slip}を入力してください`,
    computedPaymentDates: '締め日＆支払日を計算する',
    cannotComputePaymentDates: '計算できません',
    paymentSlipDetailsPrompt: '支払明細を編集しでください',
    addPaymentDetails: `${paymentDetailsStrings.name}を追加`,
    confirmAndSubmit: '確定する',
    cannotPreviewReason: '以下の原因でプレビューできません：',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  dialogContent: {
    ...common.flexColumn,
    rowGap: '1rem',
    padding: '1.5rem 100px',
    flexShrink: 1,
    minHeight: 0,
    overflow: 'scroll',
    maxHeight: 550,
  },
  prompt: {
    ...common.middleText,
    ...common.textGray700,
  },
  labelIndex: {
    display: 'inline-block',
    width: 36,
  },
  form: {
    ...common.flexColumn,
    flexShrink: 1,
    minHeight: 0,
  },
  resultContainer: {
    ...common.normalText,
    textAlign: 'center',
    padding: '100px 0',
  },
  paymentSlipDetailsContainer: {
    ...common.flexColumn,
  },
  paymentSlipDetailsInner: {
    ...common.flexColumn,
    padding: '9px 50px',
    rowGap: '0.5rem',
    overflow: 'scroll',
    maxHeight: 550,
  },
  paymentSlipDetailsTable: {
    ...common.middleText,
    display: 'grid',
    gridTemplateColumns: 'max-content repeat(9, auto)',
    '& > div > div': {
      padding: '1rem 0.15rem',
    },
    '& > *[role="add"]': {
      ...common.backgroundPrimaryAlpha3,
      ...common.flexInline,
      ...common.itemsCenter,
      ...common.fontWeight600,
      ...common.textPrimary,
      ...common.baseButton,
      columnGap: '0.5rem',
      gridColumn: '1 / -1',
      padding: '1rem 0.15rem',
      '&:hover': {
        ...common.backgroundPrimaryAlpha10,
      },
    },
    '& > *[role="head"]': {
      ...common.backgroundPrimaryAlpha3,
      ...common.textGray500,
      ...common.fontWeight600,
      ...common.whitespacePre,
      padding: '1rem 0.15rem',
    },
    '& > div > *[role="data"]': {
      ...common.textGray700,
      paddingTop: 'calc(1rem + 5.5px)',
    },
    '& > div > *[role="input"]': {
      ...common.textGray700,
    },
  },
  paymentSlipDetailsActions: {
    ...common.flexInline,
    ...common.itemsStart,
    ...common.justifyEnd,
    columnGap: '0.75rem',
  },
  SelectItemLabelIcon: {
    ...common.flexInline,
    filter: 'opacity(0.5)',
    '&:hover': {
      filter: 'opacity(1)',
    },
  },
}));

const PaymentSlipInfoStep: React.FC = observer(() => {
  const {
    step,
    payees,
    isArrivalNoticeTag,
    bookingInquiry,
    preAlert,
    currencyRates,
    customsRequest,
    setStep,
    onClose,
    paymentSlip,
    paymentSlipInfoForm,
    setPaymentSlipInfoForm,
    setPaymentSlipDetailsForm,
    deliveryRequest,
  } = useContext(PaymentSlipFormDialogContext);

  const { renderDialog, confirm } = useConfirmDialog();

  const form = useForm(PaymentSlipInfoForm, {
    initialOutput:
      paymentSlipInfoForm ??
      (import.meta.env.DEV && process.env.VITE_APP_STAGE !== 'e2e' && false
        ? examplePaymentSlipInfoForm
        : PaymentSlipHelper.resolvePaymentSlipInfoFormInitialOutput(preAlert, {
            isArrivalNoticeTag,
            deliveryRequest,
            currencyRates,
            payees,
          })),
  });

  const { fields } = form.root;

  const payeeIdAutoComplete = usePayeeIdAutoComplete({
    payees,
    idField: fields.payee.fields.id,
    nameEnField: fields.payee.fields.name_en,
  });

  const isOtherPayee = decodeResultEqual(fields.payee.fields.id.decodeResult, 0, false);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const renderReadOnly = (field: MobxZodField<ZodTypeAny>) => (
    <label>{unwrapDecodeResult(field.decodeResult) || '-'}</label>
  );

  useEffect(() => {
    if (form.isDirty) {
      const payee = payees.find((payee) =>
        decodeResultEqual(fields.payee.fields.id.decodeResult, payee.id),
      );

      if (payee) {
        fields.payee.setOutput(payee);
      } else if (decodeResultEqual(fields.payee.fields.id.decodeResult, 0)) {
        runInAction(() => {
          fields.payment_type.setOutput('現払い');
          if (!decodeResultEqual(fields.payee.fields.fc_no.decodeResult, 'RANDOM')) {
            fields.payee.setOutput({
              id: 0,
              name_ja: '',
              name_en: '',
              fc_no: 'RANDOM',
              principal_name: '',
              phone: '',
              email: '',
              partner_id: 0,
              registration_number: '',
            });
          }
        });
      }
    }
  }, [mapDecodeResult(fields.payee.fields.id.decodeResult, (d) => d, -1)]);

  pipe(
    mapDecodeResult(fields.payment_type.decodeResult, (d) => d, '' as const),
    (payment_type) => {
      useEffect(() => {
        if (form.isDirty) {
          fields.cutoff_date.setOutput('');
          fields.payment_date.setOutput(empty);
        }
      }, [payment_type, mapDecodeResult(fields.payee.decodeResult, (d) => d.id, -1)]);
    },
  );

  const getDefaultPaymentDetails = useMutation(async (form: PaymentSlipInfoForm) => {
    return await dashboardPaymentSlipApis.client.get(
      '/dashboard/payment-slip/default-payment-details',
      {
        queries: {
          payee_id: form.payee.id,
          payment_type: form.payment_type,
          d2d_no: bookingInquiry.d2d_no,
          editing_id: paymentSlip?.id,
        },
      },
    );
  });

  const getPaymentSlipPaymentDates = useMutation(async (queries: PaymentSlipPaymentDatesQuery) => {
    return await dashboardPaymentSlipApis.client.get('/dashboard/payment-slip/payment-dates', {
      queries,
    });
  });

  const onComputePaymentDates = useCallback(async () => {
    runInAction(() => {
      fields.payee.fields.id.setTouched(true);
      fields.invoice_date.setTouched(true);
    });

    const body: PaymentSlipPaymentDatesQuery = {
      payment_type: mapDecodeResult(fields.payment_type.decodeResult, id, '現払い'),
      payee_id: mapDecodeResult(fields.payee.fields.id.decodeResult, id, 0),
      invoice_date: mapDecodeResult(fields.invoice_date.decodeResult, id, ''),
    };

    await foldMessagesEither<Promise<any>>(
      PaymentSlipHelper.canComputePaymentDates(body),
      async ({ messages }) => {
        await confirm({
          title: strings.cannotComputePaymentDates,
          message: messages.join('\n'),
        });
        await form.handleSubmit(() => {});
      },
      async () => {
        const data = await getPaymentSlipPaymentDates.mutateAsync(body);
        runInAction(() => {
          fields.cutoff_date.setOutput(data.cutoff_date);
          fields.payment_date.setOutput(data.payment_date);
        });
      },
    );
  }, [bookingInquiry, preAlert, fields]);

  const { renderAlertsAndSpinnerModal } = useFormUtils({
    mutations: [getDefaultPaymentDetails, getPaymentSlipPaymentDates],
  });

  return (
    <form
      {...form.bindForm({
        async onSubmit(data) {
          // If the payee has changed, prefill the next form
          if (
            data.payee.id !== paymentSlipInfoForm?.payee.id ||
            data.payment_type !== paymentSlipInfoForm?.payment_type
          ) {
            // If not an other payee
            if (data.payee.id) {
              const result = await getDefaultPaymentDetails.mutateAsync(data);
              setPaymentSlipDetailsForm(BasePaymentDetailsHelper.mapDefaultsToForm(result));
            } else {
              setPaymentSlipDetailsForm([]);
            }
          }

          setStep(step + 1);
          setPaymentSlipInfoForm(data);
        },
        onSubmitError(errors) {
          console.error(JSON.stringify(form.rawInput));
          console.error(errors);
        },
      })}
      className={clsx(classes.form)}
    >
      {renderDialog()}

      <div className={classes.dialogContent}>
        <IndexedFormContainer>
          <div className={classes.colSpanFull}>{renderAlertsAndSpinnerModal()}</div>

          <IndexedLabel index={1} field={fields.payment_slip_no} />
          {renderReadOnly(fields.payment_slip_no)}

          <IndexedLabel index={2} label={preAlertStrings.job_code} />
          <label>{preAlert.job_code || '-'}</label>

          <IndexedLabel index={3} label={customsRequestStrings.check_type} />
          <label>{customsRequest?.check_type ?? '-'}</label>

          <IndexedLabel index={4} field={fields.payee.fields.id} required />
          <div>
            <TextInput
              field={fields.payee.fields.name_en}
              autoCompleteConfig={{
                ...payeeIdAutoComplete,
                autoCompleteOptions: payeeIdAutoComplete.autoCompleteOptions.map((o) => ({
                  ...o,
                  labelIcon: o.value.partner_id && <PartnerLink partnerId={o.value.partner_id} />,
                })),
              }}
            />
          </div>

          <IndexedLabel field={fields.payee.fields.fc_no} />
          <div>
            <TextInput field={fields.payee.fields.fc_no} disabled />
          </div>

          <IndexedLabel field={fields.payee.fields.name_ja} required={isOtherPayee} />
          <div>
            <TextInput field={fields.payee.fields.name_ja} disabled={!isOtherPayee} />
          </div>

          <IndexedLabel field={fields.payee.fields.registration_number} required={isOtherPayee} />
          <div>
            <TextInput field={fields.payee.fields.registration_number} disabled={!isOtherPayee} />
          </div>

          <IndexedLabel field={fields.payee.fields.principal_name} />
          <div>
            <TextInput field={fields.payee.fields.principal_name} disabled={!isOtherPayee} />
          </div>

          <IndexedLabel field={fields.payee.fields.phone} />
          <div>
            <TextInput field={fields.payee.fields.phone} disabled={!isOtherPayee} />
          </div>

          <IndexedLabel field={fields.payee.fields.email} />
          <div>
            <TextInput field={fields.payee.fields.email} disabled={!isOtherPayee} />
          </div>

          <IndexedLabel field={fields.payment_type} required />
          <div>
            <TextInput field={fields.payment_type} disabled={isOtherPayee} />
          </div>

          <IndexedLabel />
          <div>
            <Button
              color="primary"
              text={strings.computedPaymentDates}
              size="small"
              type="button"
              className={classes.selfStart}
              disabled={getDecodeResult(fields.payee.fields.id.decodeResult) === undefined}
              onClick={onComputePaymentDates}
            />
          </div>

          <IndexedLabel field={fields.cutoff_date} required />
          <div>
            <TextInput field={fields.cutoff_date} />
          </div>

          <IndexedLabel field={fields.payment_date} required />
          <div>
            <TextInput field={fields.payment_date} />
          </div>

          <IndexedLabel index={5} field={fields.invoice_no} />

          <div>
            <TextInput field={fields.invoice_no} />
          </div>

          <IndexedLabel index={6} field={fields.invoice_date} required />

          <div>
            <TextInput field={fields.invoice_date} />
          </div>

          <IndexedLabel index={7} field={fields.invoice_file} />

          <div>
            <FileInput field={fields.invoice_file} />
          </div>

          <IndexedLabel index={8} field={fields.memo} />

          <div>
            <TextInput field={fields.memo} multiline />
          </div>

          <IndexedLabel index={9} field={fields.arrival_notice_tag} required />
          <div>
            <TextInput
              field={fields.arrival_notice_tag}
              disabled={isArrivalNoticeTag != null || !!paymentSlip || !!deliveryRequest}
              inputOptions={{
                type: 'choose',
                options: [
                  { label: paymentSlipStrings.arrival_notice_tag_true, value: true },
                  { label: paymentSlipStrings.arrival_notice_tag_false, value: false },
                ],
              }}
            />
          </div>
        </IndexedFormContainer>
      </div>

      <ActionableDialogActions justifyContent="center">
        <Button
          color="primary"
          size="small"
          shape="round"
          type="button"
          text={formStrings.prev}
          style={{ width: 90 }}
          onClick={onClose}
        />
        <Button
          color="primary"
          size="small"
          shape="round"
          type="submit"
          text={formStrings.next}
          style={{ width: 90 }}
        />
        <DebugForm form={form} />
      </ActionableDialogActions>
    </form>
  );
});

const PaymentSlipDetailsRow: React.FC<{
  index: number;
  field: MobxZodObjectField<(typeof PaymentSlipDetailsForm)['element']>;
  editing: boolean;
  toggleEditing: () => void;
  onDelete: () => void;
}> = observer(({ index, field, editing, toggleEditing, onDelete }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { fields } = field;

  const { paymentSlipDetailsMeta, invoiceDetails } = useContext(PaymentSlipFormDialogContext);

  const helper = useMemo(
    () => mapDecodeResult(field.decodeResult, (d) => new BasePaymentDetailsHelper(d), undefined),
    [field.decodeResult],
  );

  const isTatekae = useMemo(
    () =>
      paymentSlipDetailsMeta.some(
        (m) => m.debit === '立替金' && m.cost_id === helper?.data.cost_id,
      ),
    [paymentSlipDetailsMeta, helper?.data.cost_id],
  );

  const costIdOptions = useMemo(
    () => ({
      type: 'choose' as const,
      options: paymentSlipDetailsMeta.map((m) => ({
        label: m.name,
        value: m.cost_id,
      })),
    }),
    [paymentSlipDetailsMeta, isTatekae],
  );

  const unitNumTypeOptions = useMemo(
    () => ({
      type: 'choose' as const,
      options: paymentSlipDetailsMeta
        .find((c) => decodeResultEqual(fields.cost_id.decodeResult, c.cost_id))
        ?.unit_num_types.map((t) => ({
          label: t,
          value: t,
        })),
    }),
    [paymentSlipDetailsMeta, mapDecodeResult(fields.cost_id.decodeResult, id, undefined)],
  );

  const debitOptions: TextInputOption[] = useMemo(() => {
    return Object.values(ledgerDivisions)
      .filter((v) => v.type === 'payment')
      .map((v) => ({ label: v.debit, value: v.debit }));
  }, []);

  const costIdSelected = fields.cost_id.decodeResult.success;

  const errorDisplay: React.ReactNode = (
    <span className={classes.textError}>{formStrings.error}</span>
  ) as React.ReactNode;

  return (
    <>
      <div data-testid="paymentSlipDetailEach" style={{ display: 'contents' }}>
        <div role="data" className={classes.textAlignCenter} data-testid="paymentSlipDetailNo">
          {index + 1}
        </div>

        {editing && (
          <>
            <div role="input" data-testid="paymentSlipDetailName">
              <TextInput
                field={fields.cost_id}
                inputOptions={costIdOptions}
                withErrorMessages={false}
                onSelect={() => {
                  const cost_id = unwrapDecodeResult(fields.cost_id.decodeResult);
                  const { excise_tax_type, debit, unit_num, unit_num_types } =
                    paymentSlipDetailsMeta.find((c) => c.cost_id === cost_id) ?? {};
                  if (excise_tax_type && debit) {
                    fields.excise_tax_type.setOutput(excise_tax_type);
                    fields.debit.setOutput(debit);
                  }
                  if (unit_num_types && unit_num_types.length >= 1) {
                    fields.unit_num_type.setOutput(unit_num_types[0]);
                  } else {
                    fields.unit_num_type.setOutput(empty);
                  }

                  if (unit_num != null) {
                    fields.unit_num.setOutput(unit_num);
                  }

                  const tatekaeInvoiceDetails =
                    debit === '立替金'
                      ? invoiceDetails.find((d) => d.expense_cost === cost_id)
                      : undefined;

                  if (cost_id === 123) {
                    // If 空き(123), set unit_num and unit_price to 0
                    fields.unit_num.setOutput(0);
                    fields.unit_price.setOutput(0);
                  } else if (tatekaeInvoiceDetails) {
                    fields.unit_num.setOutput(tatekaeInvoiceDetails.unit_num ?? 0);
                    fields.unit_price.setOutput(tatekaeInvoiceDetails.unit_price ?? 0);
                    fields.unit_num_type.setOutput(tatekaeInvoiceDetails.unit_num_type);
                  }
                }}
              />
            </div>

            <div role="input" data-testid="paymentSlipDetailDebit">
              <TextInput
                field={fields.debit}
                withErrorMessages={false}
                disabled={isTatekae}
                inputOptions={{
                  type: 'choose',
                  options: debitOptions,
                }}
              />
            </div>

            <div role="input" data-testid="paymentSlipDetailNamePreparation">
              <TextInput field={fields.memo} withErrorMessages={false} />
            </div>

            <div role="input" data-testid="paymentSlipDetailUnitPrice">
              <TextInput field={fields.unit_price} withErrorMessages={false} disabled={isTatekae} />
            </div>

            <div role="input" data-testid="paymentSlipDetailUnitNum">
              <TextInput field={fields.unit_num} withErrorMessages={false} disabled={isTatekae} />
            </div>

            <div role="input" data-testid="paymentSlipDetailUnitNumType">
              <TextInput
                field={fields.unit_num_type}
                inputOptions={unitNumTypeOptions}
                withErrorMessages={false}
                disabled={!costIdSelected || isTatekae}
              />
            </div>
          </>
        )}

        {!editing && (
          <>
            <div role="data" data-testid="paymentSlipDetailName">
              {mapDecodeResult(
                fields.cost_id.decodeResult,
                (d) => costIdOptions.options.find((o) => o.value === d)?.label ?? '-',
                errorDisplay,
              )}
            </div>

            <div role="data" data-testid="paymentSlipDetailDebit">
              {mapDecodeResult(fields.debit.decodeResult, id, errorDisplay)}
            </div>
            <div role="data" data-testid="paymentSlipDetailNamePreparation">
              {mapDecodeResult(fields.memo.decodeResult, id, errorDisplay)}
            </div>
            <div role="data" data-testid="paymentSlipDetailUnitPrice">
              {mapDecodeResult(
                fields.unit_price.decodeResult,
                moneyStrings.formatYenFront,
                errorDisplay,
              )}
            </div>
            <div role="data" data-testid="paymentSlipDetailUnitNum">
              {mapDecodeResult(fields.unit_num.decodeResult, id, errorDisplay)}
            </div>
            <div role="data" data-testid="paymentSlipDetailUnitNumType">
              {mapDecodeResult(fields.unit_num_type.decodeResult, id, errorDisplay)}
            </div>
          </>
        )}

        <div role="data" data-testid="paymentSlipDetailExcludedPrice">
          {mapOptional(helper?.getTaxExcludedTotalPrice(), (a) => moneyStrings.formatYenFront(a)) ??
            '-'}
        </div>

        {editing && (
          <div role="input" data-testid="paymentSlipDetailExciseTax">
            <TextInput
              field={fields.excise_tax_type}
              withErrorMessages={false}
              disabled={isTatekae}
            />
          </div>
        )}

        {!editing && (
          <div role="data" data-testid="paymentSlipDetailExciseTax">
            {mapOptional(helper?.getExciseTax(), (a) => moneyStrings.formatYenFront(a)) ?? '-'}
          </div>
        )}

        <div role="data" className={classes.paymentSlipDetailsActions}>
          <button type="button" onClick={toggleEditing} data-testid="paymentSlipDetailEdit">
            {editing && (
              <img
                src={refreshLightSvg}
                alt="edit"
                style={{ width: 18, height: 18, position: 'relative', top: 1, left: 3 }}
              />
            )}

            {!editing && (
              <img
                src={pencilGraySvg}
                alt="edit"
                style={{ width: 16, height: 16, position: 'relative', top: 2 }}
              />
            )}
          </button>

          <button type="button" onClick={onDelete} data-testid="paymentSlipDetailDelete">
            <img src={deleteSvg} alt="Delete" style={{ width: 21, height: 21 }} />
          </button>
        </div>
      </div>
    </>
  );
});

const PaymentSlipDetailsStep: React.FC = observer(() => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const {
    step,
    setStep,
    preAlert,
    bookingInquiry,
    customsRequest,
    paymentSlipInfoForm,
    setPaymentSlipDetailsForm,
    paymentSlipDetailsForm,
    currencyRates,
    paymentSlipDetailsMeta,
    setPreviewPdfUrl,
  } = useContext(PaymentSlipFormDialogContext);

  const form = useForm(PaymentSlipDetailsForm, {
    initialOutput:
      paymentSlipDetailsForm ?? (!import.meta.env.DEV ? examplePaymentSlipDetailsForm : [empty]),
  });

  const { editingIds } = useLocalObservable(() => {
    // Make all payment details that debit !== '立替金' editing
    const nonTatekae = (paymentSlipDetailsForm ?? []).filter(
      (details) =>
        paymentSlipDetailsMeta.find((meta) => meta.cost_id === details.cost_id)?.debit !== '立替金',
    );

    return {
      editingIds: new Set<number>(
        form.root.elements
          .filter(
            (element) =>
              !element.fields.cost_id.decodeResult.success ||
              nonTatekae.some((details) =>
                decodeResultEqual(element.fields.cost_id.decodeResult, details.cost_id),
              ),
          )
          .map((e) => e.uniqueId),
      ),
    };
  });

  const getLabel = (k: keyof (typeof form)['root']['type']['element']['shape']) => {
    return form.root.type.element.shape[k].getFormMeta().label;
  };

  const postPreviewPdf = dashboardPdfApis.usePost('/dashboard/pdf/preview/PaymentSlip');

  const { renderAlertsAndSpinnerModal } = useFormUtils({ mutations: [postPreviewPdf] });

  return (
    <form
      className={classes.paymentSlipDetailsContainer}
      {...form.bindForm({
        async onSubmit(data) {
          setPaymentSlipDetailsForm(data);

          const pdfBlob = await postPreviewPdf.mutateAsync({
            ...paymentSlipInfoForm!,
            pre_alert: preAlert,
            booking_inquiry: bookingInquiry,
            conversion_rate:
              currencyRates.find((c) => c.date === paymentSlipInfoForm?.conversion_rate_date)
                ?.rate ?? 0,
            payment_details: data.map((d) => ({
              ...d,
              name: paymentSlipDetailsMeta.find((p) => p.cost_id === d.cost_id)!.name,
              credit: Object.values(ledgerDivisions).find((division) => division.debit === d.debit)!
                .debit,
              excise_tax: new BasePaymentDetailsHelper(d).getExciseTax(),
            })),
            invoice_file:
              paymentSlipInfoForm?.invoice_file &&
              FileTypeHelper.localToRemote(paymentSlipInfoForm?.invoice_file),
            customs_request: customsRequest,
          });

          setPreviewPdfUrl(URL.createObjectURL(pdfBlob));
          setStep(step + 1);
        },
      })}
    >
      <div className={classes.paymentSlipDetailsInner}>
        {renderAlertsAndSpinnerModal()}

        <div className={clsx(classes.middleText, classes.textGray700)}>
          {strings.paymentSlipDetailsPrompt}
        </div>

        <div className={classes.paymentSlipDetailsTable}>
          <div role="head" style={{ paddingLeft: '1rem' }}>
            No.
          </div>
          <div role="head" style={{ minWidth: 180 }}>
            {getLabel('cost_id')}
          </div>
          <div role="head" style={{ minWidth: 90 }}>
            {getLabel('debit')}
          </div>
          <div role="head" style={{ minWidth: 100 }}>
            {getLabel('memo')}
          </div>
          <div role="head" style={{ minWidth: 50 }}>
            {getLabel('unit_price')}
          </div>
          <div role="head">{getLabel('unit_num')}</div>
          <div role="head">{getLabel('unit_num_type')}</div>{' '}
          <div role="head" style={{ paddingRight: 16 }}>
            {paymentDetailsStrings.non_tax_amount}
          </div>
          <div role="head" style={{ minWidth: 80 }}>
            {getLabel('excise_tax_type')}
          </div>
          <div role="head" style={{ minWidth: 80 }} />
          {form.root.elements.map((element, i) => (
            <PaymentSlipDetailsRow
              key={element.uniqueId}
              index={i}
              field={element}
              editing={editingIds.has(element.uniqueId)}
              toggleEditing={async () => {
                if (!editingIds.has(element.uniqueId)) {
                  runInAction(() => {
                    editingIds.add(element.uniqueId);
                  });
                } else {
                  // To trigger a validation
                  await form.handleSubmit(() => {});

                  if (Object.values(element.fields).every((f) => f.errorMessages.length === 0)) {
                    runInAction(() => {
                      editingIds.delete(element.uniqueId);
                    });
                  }
                }
              }}
              onDelete={() => form.root.splice(i, 1, [])}
            />
          ))}
          <ErrorMessages field={form.root} />
          <div
            role="add"
            onClick={() => {
              form.root.push(PaymentSlipHelper.getPaymentSlipDetailsFormRowInitialValue());
              mapOptional(form.root.elements.at(-1)?.uniqueId, (id) => editingIds.add(id));
            }}
            style={{
              paddingLeft: '1rem',
              marginTop: form.root.elements.length === 0 ? '0.5rem' : 0,
            }}
          >
            <img src={addSvg} alt="Add" />
            {strings.addPaymentDetails}
          </div>
        </div>
      </div>

      <ActionableDialogActions justifyContent="center">
        <Button
          color="primary"
          size="small"
          shape="round"
          type="button"
          text={formStrings.prev}
          style={{ width: 90 }}
          onClick={() => setStep(step - 1)}
        />
        <Button
          color="primary"
          size="small"
          shape="round"
          type="submit"
          text={formStrings.next}
          style={{ width: 90 }}
        />
        <DebugForm form={form} />
      </ActionableDialogActions>
    </form>
  );
});

export const PaymentSlipPreviewStep = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const {
    type,
    previewPdfUrl,
    step,
    setStep,
    paymentSlip,
    paymentSlipInfoForm,
    paymentSlipDetailsForm,
    bookingInquiry,
    payees,
    currencyRates,
    paymentSlipDetailsMeta,
    onClose,
    deliveryRequest,
  } = useContext(PaymentSlipFormDialogContext);

  const queryClient = useQueryClient();

  const onMutationSuccess = () => {
    queryClient.invalidateQueries(
      dashboardPaymentSlipApis.getKeyByPath('get', '/dashboard/payment-slip'),
    );
    queryClient.invalidateQueries(
      dashboardPaymentSlipApis.getKeyByPath('get', '/dashboard/payment-slip/:id'),
    );
    queryClient.invalidateQueries(
      dashboardPdfApis.getKeyByPath('get', '/dashboard/pdf/PaymentSlip/:id'),
    );
    queryClient.invalidateQueries(
      dashboardBookingInquiryApis.getKeyByPath(
        'get',
        '/dashboard/booking-inquiry/:d2d_no/status-flows',
      ),
    );
  };

  const fileUpload = fileUploadApis.useMutation('post', '/file/upload');

  const postPaymentSlipDraft = dashboardPaymentSlipApis.usePost(
    '/dashboard/payment-slip',
    {
      queries: {},
    },
    {
      onSuccess: onMutationSuccess,
    },
  );

  const postPaymentSlipSent = dashboardPaymentSlipApis.usePost(
    '/dashboard/payment-slip',
    {
      queries: {
        action: 'SEND',
      },
    },
    {
      onSuccess: onMutationSuccess,
    },
  );

  const patchPaymentSlip = dashboardPaymentSlipApis.usePatch(
    '/dashboard/payment-slip/:id',
    {
      params: {
        id: paymentSlip?.id!,
      },
    },
    {
      onSuccess: onMutationSuccess,
    },
  );

  const putPaymentSlip = dashboardPaymentSlipApis.usePut(
    '/dashboard/payment-slip/:id',
    {
      queries: {
        action: 'SEND',
      },
      params: {
        id: paymentSlip?.id!,
      },
    },
    {
      onSuccess: onMutationSuccess,
    },
  );

  const { renderAlertsAndSpinnerModal } = useFormUtils({
    mutations: [fileUpload, postPaymentSlipSent, postPaymentSlipDraft, patchPaymentSlip],
  });

  const maybePostPaymentSlip = useMemo(
    () =>
      PaymentSlipHelper.maybeMapFormToPost(paymentSlipInfoForm!, paymentSlipDetailsForm!, {
        bookingInquiry,
        payees,
        currencyRates,
        paymentSlipDetailsMeta,
      }),
    [
      paymentSlipInfoForm,
      paymentSlipDetailsForm,
      bookingInquiry,
      payees,
      currencyRates,
      paymentSlipDetailsMeta,
    ],
  );

  const mutatePaymentSlip = async (status: 'draft' | 'sent') => {
    const [invoice_file] = paymentSlipInfoForm!.invoice_file
      ? await fileUpload.mutateAsync({
          file: [paymentSlipInfoForm!.invoice_file.file],
        })
      : [null];

    const maybePostPaymentSlip = PaymentSlipHelper.maybeMapFormToPost(
      paymentSlipInfoForm!,
      paymentSlipDetailsForm!,
      {
        bookingInquiry,
        payees,
        currencyRates,
        paymentSlipDetailsMeta,
        invoice_file,
        deliveryRequest,
      },
    );

    if (maybePostPaymentSlip._tag === 'Right') {
      if (type === 'create') {
        if (status === 'draft') {
          await postPaymentSlipDraft.mutateAsync(maybePostPaymentSlip.right);
        } else {
          await postPaymentSlipSent.mutateAsync(maybePostPaymentSlip.right);
        }
      } else {
        await patchPaymentSlip.mutateAsync(maybePostPaymentSlip.right);
        if (status === 'sent') {
          await putPaymentSlip.mutateAsync(undefined);
        }
      }
      onClose();
    }
  };

  return (
    <div className={clsx(classes.form)}>
      <div className={classes.dialogContent}>
        {maybePostPaymentSlip._tag === 'Right' ? (
          <>
            {renderAlertsAndSpinnerModal()}

            <div className={classes.flexColumnCenter}>
              {previewPdfUrl && (
                <Document
                  file={previewPdfUrl}
                  loading={
                    <div className={classes.flexColumnCenter} style={{ height: 550 }}>
                      Loading...
                    </div>
                  }
                >
                  <Page width={900} pageNumber={1} />
                </Document>
              )}
            </div>
          </>
        ) : (
          <div>
            <div>{strings.cannotPreviewReason}</div>
            {maybePostPaymentSlip.left.messages.map((m, i) => (
              <div key={i}>{m}</div>
            ))}
          </div>
        )}
      </div>

      <ActionableDialogActions justifyContent="center">
        <Button
          color="primary"
          size="small"
          shape="round"
          type="button"
          text={formStrings.prev}
          style={{ width: 90 }}
          onClick={() => setStep(step - 1)}
        />
        <Button
          color="primary"
          size="small"
          shape="round"
          type="button"
          text={formStrings.temporarilySave}
          disabled={
            maybePostPaymentSlip._tag === 'Left' ||
            paymentSlip?.status[paymentSlip?.status.length - 1].name === '修正中'
          }
          style={{ width: 90 }}
          onClick={() => mutatePaymentSlip('draft')}
        />

        <Button
          color="primary"
          size="small"
          shape="round"
          type="button"
          disabled={maybePostPaymentSlip._tag === 'Left'}
          text={
            <>
              <img src={lockIcon} alt="Lock" style={{ paddingRight: '0.2rem' }} />
              {strings.confirmAndSubmit}
            </>
          }
          style={{ width: 100 }}
          onClick={() => mutatePaymentSlip('sent')}
        />
      </ActionableDialogActions>
    </div>
  );
};

export interface PaymentSlipFormDialogProps extends PaymentSlipFormDialogContextValuesBase {}

export const PaymentSlipFormDialog: React.FC<PaymentSlipFormDialogProps> = observer((props) => {
  const { open, onClose, bookingInquiry, paymentSlip } = props;

  const payeesQuery = dashboardPaymentSlipApis.useGet('/dashboard/payment-slip/payee', undefined, {
    staleTime: Infinity,
    enabled: open,
    onSuccess(data) {
      runInAction(() => {
        context.payees = data;
      });
    },
  });

  const paymentSlipDetailsMetaQuery = dashboardPaymentSlipApis.useGet(
    '/dashboard/payment-slip/payment-details-meta',
    {
      queries: {
        d2d_no: bookingInquiry.d2d_no,
      },
    },
    {
      enabled: open,
      staleTime: Infinity,
      onSuccess(data) {
        runInAction(() => {
          context.paymentSlipDetailsMeta = data;
        });
      },
    },
  );

  const currencyRatesQuery = exchangeCalendarApis.useGet(
    '/exchange-calendar',
    {
      queries: {
        pair: 'JPY_USD',
        start: dayjs(bookingInquiry.created_at).format('YYYY-MM-DD'),
        end: dayjs(bookingInquiry.created_at).add(1, 'month').format('YYYY-MM-DD'),
      },
    },
    {
      enabled: open,
      onSuccess(data) {
        runInAction(() => {
          context.currencyRates = data;
        });
      },
    },
  );

  const customsRequestQuery = dashboardCustomsRequestApis.useGet(
    '/dashboard/customs-request/:d2d_no',
    {
      params: { d2d_no: bookingInquiry.d2d_no },
    },
    {
      enabled: open,
      onSuccess(data) {
        runInAction(() => {
          if (data) {
            context.customsRequest = data;
          }
        });
      },
    },
  );

  const invoiceDetailsQuery = dashboardInvoiceApis.useGet(
    '/dashboard/booking-inquiry/:d2d_no/invoice-detail',
    {
      params: {
        d2d_no: bookingInquiry.d2d_no,
      },
      queries: { show_all: true },
    },
    {
      enabled: open,
      onSuccess(data) {
        runInAction(() => {
          context.invoiceDetails = data;
        });
      },
    },
  );

  const context = useLocalObservable(
    (): PaymentSlipFormDialogContextValues => ({
      ...props,
      step: 0,
      setStep(step) {
        this.step = step;
      },
      payees: payeesQuery.data ?? [],
      paymentSlipDetailsMeta: paymentSlipDetailsMetaQuery.data ?? [],
      currencyRates: currencyRatesQuery.data ?? [],
      customsRequest: customsRequestQuery.data ?? undefined,
      paymentSlipInfoForm: mapOptional(paymentSlip, (v) => PaymentSlipHelper.mapToInfoForm(v)),
      paymentSlipDetailsForm: mapOptional(paymentSlip, (v) =>
        PaymentSlipHelper.mapToDetailsForm(v),
      ),
      invoiceDetails: [],
      setPaymentSlipInfoForm(v) {
        this.paymentSlipInfoForm = v;
      },
      setPaymentSlipDetailsForm(v) {
        this.paymentSlipDetailsForm = v;
      },
      setPreviewPdfUrl(v) {
        this.previewPdfUrl = v;
      },
    }),
  );

  return (
    <PaymentSlipFormDialogContext.Provider value={context}>
      <ActionableDialog
        open={open}
        onClose={onClose}
        size="xl"
        source={import.meta.url}
        muiDialogProps={{ disablePortal: false }}
      >
        <ActionableDialogTitle textAlign="center">
          {strings.title()[context.type]}
        </ActionableDialogTitle>

        <QueryContainer
          queries={[
            payeesQuery,
            paymentSlipDetailsMetaQuery,
            currencyRatesQuery,
            invoiceDetailsQuery,
          ]}
        >
          {context.step === 0 && <PaymentSlipInfoStep />}
          {context.step === 1 && <PaymentSlipDetailsStep />}
          {context.step === 2 && <PaymentSlipPreviewStep />}
        </QueryContainer>
      </ActionableDialog>
    </PaymentSlipFormDialogContext.Provider>
  );
});
